import React, { useState, useEffect, useRef, useCallback, JSX } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import ProfilePictureHover from '../../components/ProfilePictureHover';
import PayrollManagementSkeletonLoader from '../../skeletons/PayrollManagementSkeletonLoader';
import PayrollSalaryModal from './parts/PayrollSalaryModal';
import { fmt, fmtMonth, THIS_MONTH, THIS_SESSION } from './parts/payrollUtils';

// re-export so existing imports from parts still work
export { fmt, fmtMonth, THIS_MONTH, THIS_SESSION };

// ─────────────────────────────────────────────────────────────────────────────
// Inline SVG icons — kept EXACTLY as before. PayrollRecords, PayrollAdvances,
// GeneratePayrollPanel, and/or PayrollSalaryModal import these directly from
// this file, so they must stay exported with the same names/signatures even
// though the new shell below uses PrimeIcons ('pi pi-...') instead.
// ─────────────────────────────────────────────────────────────────────────────
const Ico = ({ d, size = 16, color = 'currentColor', style = {}, strokeWidth = 1.8 }: {
  d: string | string[]; size?: number; color?: string; style?: React.CSSProperties; strokeWidth?: number;
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'block', ...style }}>
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

export const IcoDollar   = (p: any) => <Ico {...p} d={['M12 1v22', 'M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6']} />;
export const IcoUsers    = (p: any) => <Ico {...p} d={['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M9 11a4 4 0 100-8 4 4 0 000 8z', 'M23 21v-2a4 4 0 00-3-3.87', 'M16 3.13a4 4 0 010 7.75']} />;
export const IcoCheck    = (p: any) => <Ico {...p} d="M20 6L9 17l-5-5" />;
export const IcoAlert    = (p: any) => <Ico {...p} d={['M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z', 'M12 9v4', 'M12 17h.01']} />;
export const IcoChevL    = (p: any) => <Ico {...p} d="M15 18l-6-6 6-6" />;
export const IcoChevR    = (p: any) => <Ico {...p} d="M9 18l6-6-6-6" />;
export const IcoFilter   = (p: any) => <Ico {...p} d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />;
export const IcoRefresh  = (p: any) => <Ico {...p} d={['M23 4v6h-6', 'M1 20v-6h6', 'M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15']} />;
export const IcoList     = (p: any) => <Ico {...p} d={['M8 6h13', 'M8 12h13', 'M8 18h13', 'M3 6h.01', 'M3 12h.01', 'M3 18h.01']} />;
export const IcoPlus     = (p: any) => <Ico {...p} d={['M12 5v14', 'M5 12h14']} />;
export const IcoEdit     = (p: any) => <Ico {...p} d={['M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7', 'M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z']} />;
export const IcoTrash    = (p: any) => <Ico {...p} d={['M3 6h18', 'M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2']} />;
export const IcoDownload = (p: any) => <Ico {...p} d={['M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3']} />;
export const IcoCalendar = (p: any) => <Ico {...p} d={['M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z', 'M16 2v4', 'M8 2v4', 'M3 10h18']} />;
export const IcoX        = (p: any) => <Ico {...p} d={['M18 6L6 18', 'M6 6l12 12']} />;
export const IcoBarChart = (p: any) => <Ico {...p} d={['M18 20V10', 'M12 20V4', 'M6 20v-6']} />;
export const IcoClock    = (p: any) => <Ico {...p} d={['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M12 6v6l4 2']} />;
export const IcoZap      = (p: any) => <Ico {...p} d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />;
export const IcoArrowDL  = (p: any) => <Ico {...p} d={['M12 5v14', 'M19 12l-7 7-7-7']} />;

// ─────────────────────────────────────────────────────────────────────────────
// NOTE ON THIS RESTYLE
// This file now hosts TWO page-level components instead of one tabbed page:
//
//   - PayrollManagement (default export)  → "Salary Setup" page, routed at
//     /payroll-management, matching the sidebar's "Salary Setup" entry.
//   - PayrollRecordsPage (named export)   → "Payroll Records" page, routed at
//     /payroll-management/records, matching the sidebar's "Payroll Records"
//     entry. It internally keeps a small Records/Advances tab pair, mirroring
//     the previous in-page tabs but now scoped to its own route.
//
// Both share the same visual language (GlobalHeaderTitle, ps-* shell classes,
// pr-* legacy classes) so they still look like one cohesive module even
// though they're reached from two separate sidebar links, the same way
// DateSheet.tsx and AwardRole.tsx are two routes under one "Exams" group.
//
// PayrollRecords, PayrollAdvances, GeneratePayrollPanel and
// PayrollSalaryModal live in separate files that weren't provided, so they
// still render unchanged — PayrollRecords/PayrollAdvances now render inside
// PayrollRecordsPage instead of inside PayrollManagement. The legacy `CSS`
// export (pr-* classes) and the `SHELL_CSS` export (ps-* classes) are kept
// and injected by both components below so nothing collides.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SalaryRow {
  id: string;
  employee_id: string;
  employee_code?: string | null;
  employee_role: 'teacher' | 'staff' | 'principal';
  employee_name: string | null;
  employee_contact: string | null;
  profile_picture: string | null;
  role: string | null;
  basic_salary: number;
  hra: number;
  transport_allowance: number;
  medical_allowance: number;
  other_allowance: number;
  pf_type: 'percentage' | 'fixed';
  provident_fund_pct: number;
  provident_fund_amount: number;
  professional_tax: number;
  tds_pct: number;
  effective_from: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}



export type EmployeeRole = 'teacher' | 'staff' | 'principal';

export interface UngeneratedEmployee {
  employee_id: string;
  employee_code?: string | null;
  employee_role: 'teacher' | 'staff' | 'principal';
  employee_name: string | null;
  employee_contact: string | null;
  profile_picture: string | null;
  role: string | null;
}

export interface UnifiedEmployeeRow {
  id: string | null;
  employee_id: string;
  employee_code?: string | null;
  employee_role: 'teacher' | 'staff' | 'principal';
  employee_name: string | null;
  employee_contact: string | null;
  profile_picture: string | null;
  role: string | null;
  has_salary: boolean;
  basic_salary: number | null;
  hra: number | null;
  transport_allowance: number | null;
  medical_allowance: number | null;
  other_allowance: number | null;
  pf_type: 'percentage' | 'fixed' | null;
  provident_fund_pct: number | null;
  provident_fund_amount: number | null;
  professional_tax: number | null;
  tds_pct: number | null;
  effective_from: string | null;
  status: 'active' | 'inactive' | null;
  created_at: string | null;
  updated_at: string | null;
}

type SalaryStatusFilter = 'all' | 'has' | 'none';
type RecordsTab = 'records' | 'advances';

// ─────────────────────────────────────────────────────────────────────────────
// Legacy CSS — still required by PayrollRecords / PayrollAdvances /
// GeneratePayrollPanel / PayrollSalaryModal. Left unchanged.
// ─────────────────────────────────────────────────────────────────────────────
export const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&display=swap');

  .pr-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }

  @keyframes pr-fadeIn  { from{opacity:0}                            to{opacity:1} }
  @keyframes pr-slideUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pr-shimmer { 0%{background-position:-600px 0}           100%{background-position:600px 0} }
  @keyframes pr-spin    { from{transform:rotate(0deg)}                to{transform:rotate(360deg)} }
  @keyframes pr-pulse   { 0%,100%{opacity:1}                         50%{opacity:.5} }

  .pr-tab-bar {
    display:flex; gap:4px;
    background:rgba(15,118,110,.06);
    border-radius:12px; padding:4px;
  }
  .pr-tab {
    display:inline-flex; align-items:center; gap:7px;
    padding:8px 16px; border-radius:9px; border:none;
    font-size:13px; font-weight:600; cursor:pointer;
    font-family:'DM Sans',sans-serif; transition:all .15s;
    background:transparent; color:#6b7280;
  }
  .pr-tab:hover { color:#0f766e; background:rgba(15,118,110,.06); }
  .pr-tab--active { background:white; color:#0f766e; box-shadow:0 1px 6px rgba(15,118,110,.14); }
  .pr-tab--advances.pr-tab--active { color:#b45309; }
  .pr-tab--advances:hover { color:#b45309; background:rgba(245,158,11,.06); }

  .pr-table { width:100%; border-collapse:collapse; }
  .pr-table thead th {
    padding:13px 16px; background:#f9fafb; color:#6b7280;
    font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.7px;
    border-bottom:1px solid #e5e7eb; white-space:nowrap; text-align:left;
  }
  .pr-table tbody tr {
    border-bottom:1px solid #f3f4f6; transition:background .13s;
    animation:pr-slideUp .25s ease both;
  }
  .pr-table tbody tr:hover { background:rgba(15,118,110,.02); }
  .pr-table tbody tr:last-child { border-bottom:none; }
  .pr-table tbody td { padding:13px 16px; vertical-align:middle; }

  .pr-badge {
    display:inline-flex; align-items:center; gap:5px;
    padding:3px 10px; border-radius:20px; font-size:11px; font-weight:700;
    white-space:nowrap;
  }
  .pr-badge--paid      { background:rgba(34,197,94,.1);  color:#15803d; border:1px solid rgba(34,197,94,.2); }
  .pr-badge--pending   { background:rgba(245,158,11,.1); color:#b45309; border:1px solid rgba(245,158,11,.2); }
  .pr-badge--on_hold   { background:rgba(99,102,241,.1); color:#4338ca; border:1px solid rgba(99,102,241,.2); }
  .pr-badge--cancelled { background:rgba(239,68,68,.1);  color:#dc2626; border:1px solid rgba(239,68,68,.2); }
  .pr-badge--deducted  { background:rgba(34,197,94,.1);  color:#15803d; border:1px solid rgba(34,197,94,.2); }

  .pr-role {
    display:inline-block; padding:2px 8px; border-radius:6px;
    font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.5px;
    background:rgba(15,118,110,.08); color:#0f766e; border:1px solid rgba(15,118,110,.15);
  }

  .pr-role--login {
    background:rgba(99,102,241,.08); color:#4338ca; border:1px solid rgba(99,102,241,.15);
  }

  .pr-stat-card {
    background:white; border-radius:16px; padding:18px 20px;
    border:1px solid #e5e7eb; display:flex; align-items:center; gap:14px;
    box-shadow:0 2px 8px rgba(0,0,0,.05); transition:transform .15s, box-shadow .15s;
    animation:pr-slideUp .3s ease both;
  }
  .pr-stat-card:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,.09); }

  .pr-live-dot {
    width:8px; height:8px; border-radius:50%; background:#0d9488;
    animation:pr-pulse 1.5s ease-in-out infinite;
  }

  .pr-select {
    padding:8px 12px; border-radius:10px; border:1px solid #e5e7eb;
    background:white; font-size:13px; font-weight:500; color:#374151;
    font-family:'DM Sans',sans-serif; cursor:pointer; outline:none;
    transition:border-color .15s, box-shadow .15s;
  }
  .pr-select:focus { border-color:#0d9488; box-shadow:0 0 0 3px rgba(13,148,136,.12); }

  .pr-input {
    padding:8px 12px; border-radius:10px; border:1px solid #e5e7eb;
    font-size:13px; font-weight:500; color:#374151;
    font-family:'DM Sans',sans-serif; outline:none; background:white;
    transition:border-color .15s, box-shadow .15s;
  }
  .pr-input:focus { border-color:#0d9488; box-shadow:0 0 0 3px rgba(13,148,136,.12); }

  .pr-label {
    display:block; font-size:11px; font-weight:700; color:#6b7280;
    text-transform:uppercase; letter-spacing:.5px; margin-bottom:5px;
  }

  .pr-btn {
    display:inline-flex; align-items:center; gap:7px;
    padding:9px 18px; border-radius:11px; border:none;
    font-size:13px; font-weight:600; cursor:pointer;
    font-family:'DM Sans',sans-serif; transition:all .15s;
    white-space:nowrap;
  }
  .pr-btn--primary {
    background:linear-gradient(135deg,#0f766e,#0d9488); color:white;
    box-shadow:0 3px 10px rgba(15,118,110,.28);
  }
  .pr-btn--primary:hover { box-shadow:0 5px 16px rgba(15,118,110,.38); transform:translateY(-1px); }
  .pr-btn--ghost  { background:rgba(15,118,110,.07); color:#0f766e; border:1px solid rgba(15,118,110,.15); }
  .pr-btn--ghost:hover  { background:rgba(15,118,110,.13); }
  .pr-btn--danger { background:rgba(239,68,68,.08); color:#dc2626; border:1px solid rgba(239,68,68,.2); }
  .pr-btn--danger:hover { background:rgba(239,68,68,.15); }
  .pr-btn--warn   { background:rgba(245,158,11,.09); color:#b45309; border:1px solid rgba(245,158,11,.22); }
  .pr-btn--warn:hover { background:rgba(245,158,11,.17); }
  .pr-btn--amber  { background:linear-gradient(135deg,#b45309,#f59e0b); color:white; box-shadow:0 3px 10px rgba(180,83,9,.28); }
  .pr-btn--amber:hover { box-shadow:0 5px 16px rgba(180,83,9,.38); transform:translateY(-1px); }
  .pr-btn:disabled { opacity:.45; cursor:not-allowed; transform:none!important; box-shadow:none!important; }

  .pr-shimmer {
    background:linear-gradient(90deg,#f0f4f8 25%,#e2e8f0 50%,#f0f4f8 75%);
    background-size:600px 100%; animation:pr-shimmer 1.3s infinite linear; border-radius:6px;
  }

  .pr-pg-btn {
    width:34px; height:34px; border-radius:9px; border:1px solid #e5e7eb;
    background:white; cursor:pointer; display:flex; align-items:center; justify-content:center;
    font-size:13px; font-weight:600; color:#374151; transition:all .13s;
  }
  .pr-pg-btn:hover:not(:disabled) { background:rgba(15,118,110,.07); border-color:rgba(15,118,110,.2); }
  .pr-pg-btn--active { background:linear-gradient(135deg,#0f766e,#0d9488)!important; color:white!important; border-color:transparent!important; }
  .pr-pg-btn:disabled { opacity:.4; cursor:not-allowed; }

  .pr-scroll::-webkit-scrollbar { width:4px; height:4px; }
  .pr-scroll::-webkit-scrollbar-track { background:transparent; }
  .pr-scroll::-webkit-scrollbar-thumb { background:rgba(15,118,110,.15); border-radius:4px; }

  .pr-mode-pill { display:inline-flex; align-items:center; background:rgba(15,118,110,.06); border-radius:9px; padding:3px; }
  .pr-mode-btn {
    padding:5px 11px; border-radius:7px; border:none; cursor:pointer;
    font-size:11px; font-weight:700; font-family:'DM Sans',sans-serif;
    text-transform:uppercase; letter-spacing:.4px; transition:all .13s;
    background:transparent; color:#9ca3af;
  }
  .pr-mode-btn--active { background:white; color:#0f766e; box-shadow:0 1px 4px rgba(15,118,110,.12); }

  .pr-overlay {
    position:fixed; inset:0; z-index:1000; display:flex;
    align-items:center; justify-content:center; padding:16px;
    background:rgba(0,0,0,.45); animation:pr-fadeIn .18s ease;
  }
  .pr-modal {
    background:white; border-radius:20px; width:100%; max-width:800px;
    max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.18);
    animation:pr-slideUp .22s ease;
  }
  .pr-modal::-webkit-scrollbar { width:4px; }
  .pr-modal::-webkit-scrollbar-thumb { background:rgba(15,118,110,.15); border-radius:4px; }

  .pr-modal-header {
    padding:20px 24px 16px; border-bottom:1px solid #f3f4f6;
    display:flex; align-items:center; justify-content:space-between; gap:12px;
  }
  .pr-modal-body   { padding:20px 24px; display:flex; flex-direction:column; gap:16px; }
  .pr-modal-footer { padding:16px 24px; border-top:1px solid #f3f4f6; display:flex; gap:10px; justify-content:flex-end; }

  .pr-field { display:flex; flex-direction:column; gap:5px; }
  .pr-field-row { display:grid; gap:14px; }

  .pr-generate-card {
    border:1.5px dashed rgba(15,118,110,.3); border-radius:16px;
    padding:20px; background:rgba(15,118,110,.025);
    display:flex; flex-direction:column; gap:14px;
    animation:pr-slideUp .3s ease both;
  }

  .pr-pf-badge {
    display:inline-flex; align-items:center; gap:4px;
    padding:2px 8px; border-radius:6px; font-size:11px; font-weight:700;
  }
  .pr-pf-badge--pct   { background:rgba(99,102,241,.08); color:#4338ca; border:1px solid rgba(99,102,241,.18); }
  .pr-pf-badge--fixed { background:rgba(15,118,110,.08); color:#0f766e; border:1px solid rgba(15,118,110,.18); }

  .pr-advance-card {
    border:1.5px dashed rgba(245,158,11,.3); border-radius:16px;
    padding:16px 20px; background:rgba(245,158,11,.025);
    display:flex; align-items:center; justify-content:space-between;
    gap:12px; flex-wrap:wrap; animation:pr-slideUp .3s ease both;
  }

  .pr-avatar-wrap { display:flex; align-items:center; gap:10px; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// New shell styling — matches StudentsListDashboard.tsx exactly (search bar,
// tab pills, stat cards, DataTable skin, empty state, animations). Exported
// so PayrollRecordsPage (below, same file) can reuse the same tab pills.
// ─────────────────────────────────────────────────────────────────────────────
export const SHELL_CSS = `
  @keyframes slideIn   { from { opacity:0; transform:translateX(-10px) } to { opacity:1; transform:translateX(0) } }

  .ps-datatable .p-datatable-tbody > tr { animation:slideIn .35s ease-out; transition:background .15s; }
  .ps-datatable .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; box-shadow:inset 4px 0 0 0 #4169E1; }
  .ps-datatable .p-datatable-thead > tr > th { background:#f9fafb!important; color:#6b7280!important; font-size:11px!important; font-weight:700!important; text-transform:uppercase!important; letter-spacing:.8px!important; padding:16px 12px!important; border-bottom:1px solid #e5e7eb!important; }
  .ps-datatable .p-datatable-tbody > tr > td { padding:16px 12px!important; border-bottom:1px solid #f3f4f6!important; }
  .ps-datatable .p-datatable-tbody > tr:last-child > td { border-bottom:none!important; }

  .ps-search-wrapper { display:flex; align-items:center; gap:10px; background:white; border-radius:16px; padding:12px 18px; margin-bottom:20px; box-shadow:0 2px 8px rgba(30,64,175,.08); border:1px solid rgba(59,130,246,.12); transition:border-color .2s,box-shadow .2s; }
  .ps-search-wrapper:focus-within { border-color:rgba(59,130,246,.4); box-shadow:0 2px 12px rgba(30,64,175,.14); }
  .ps-search-wrapper input[type="text"] { flex:1; border:none; outline:none; font-size:13px; font-weight:500; color:#1f2937; background:transparent; }
  .ps-search-wrapper input[type="text"]::placeholder { color:#9ca3af; font-weight:400; }
  .ps-search-clear { background:none; border:none; cursor:pointer; color:#9ca3af; padding:2px 4px; border-radius:4px; display:flex; align-items:center; transition:color .2s; }
  .ps-search-clear:hover { color:#6b7280; }

  .ps-tab-bar { display:flex; gap:6px; background:white; border-radius:16px; padding:6px; margin-bottom:16px; box-shadow:0 2px 8px rgba(30,64,175,.08); border:1px solid rgba(59,130,246,.12); }
  .ps-tab { flex:1; display:inline-flex; align-items:center; justify-content:center; gap:7px; padding:10px 16px; border-radius:11px; border:none; font-size:13px; font-weight:700; cursor:pointer; transition:all .15s; background:transparent; color:#6b7280; }
  .ps-tab:hover { color:#1e40af; background:rgba(65,105,225,.06); }
  .ps-tab--active { background:linear-gradient(135deg,#1e40af,#3b82f6); color:white; box-shadow:0 3px 10px rgba(59,130,246,.28); }

  .ps-select { padding:8px 12px; border-radius:10px; border:1.5px solid #c7d2fe; background:white; font-size:12px; font-weight:600; color:#374151; cursor:pointer; outline:none; transition:border-color .15s, box-shadow .15s; }
  .ps-select:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.12); }

  .ps-add-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border-radius:10px; font-size:12px; font-weight:700; cursor:pointer; border:none; background:linear-gradient(135deg,#1e40af,#3b82f6); color:white; box-shadow:0 3px 10px rgba(59,130,246,.28); transition:all .15s; white-space:nowrap; }
  .ps-add-btn:hover { box-shadow:0 5px 16px rgba(59,130,246,.38); transform:translateY(-1px); }

  .ps-edit-btn { display:inline-flex; align-items:center; gap:5px; padding:6px 12px; border-radius:9px; font-size:12px; font-weight:600; cursor:pointer; border:1.5px solid #c7d2fe; background:white; color:#1e40af; transition:all .15s; }
  .ps-edit-btn:hover { background:#eff6ff; }
  .ps-set-btn { display:inline-flex; align-items:center; gap:5px; padding:6px 12px; border-radius:9px; font-size:12px; font-weight:600; cursor:pointer; border:1.5px solid rgba(245,158,11,.35); background:rgba(245,158,11,.08); color:#b45309; transition:all .15s; }
  .ps-set-btn:hover { background:rgba(245,158,11,.16); }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Static filter options
// ─────────────────────────────────────────────────────────────────────────────
const ROLE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All roles' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'staff', label: 'Staff' },
  { value: 'principal', label: 'Principal' },
];

const SALARY_STATUS_OPTIONS: { value: SalaryStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'has', label: 'Salary set' },
  { value: 'none', label: 'Salary not set' },
];

const ROLE_COLORS: Record<string, string> = {
  teacher: '#3b82f6',
  staff: '#8b5cf6',
  principal: '#f59e0b',
};

// ─────────────────────────────────────────────────────────────────────────────
// Restored original exported components — StatCard, Avatar, and SkeletonRow
// keep their EXACT original prop signatures below because PayrollRecords,
// PayrollAdvances, GeneratePayrollPanel, and/or PayrollSalaryModal import
// them directly from this file. The shell uses its own differently-named
// components (PsSummaryCard) and ProfilePictureHover for avatars, so nothing
// here collides.
// ─────────────────────────────────────────────────────────────────────────────
export const StatCard: React.FC<{
  label: string; value: string | number; sub?: string;
  icon: React.ReactNode; gradient: string; delay?: number;
}> = ({ label, value, sub, icon, gradient, delay = 0 }) => (
  <div className="pr-stat-card" style={{ animationDelay: `${delay}s` }}>
    <div style={{
      width: 46, height: 46, borderRadius: 13, flexShrink: 0,
      background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 4px 12px rgba(0,0,0,.18)',
    }}>{icon}</div>
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{sub}</div>}
    </div>
  </div>
);

export const Avatar: React.FC<{ src: string | null; name: string | null; size?: number }> = ({ src, name, size = 32 }) => {
  const [errored, setErrored] = useState(false);
  const initial = (name ?? '?').trim().charAt(0).toUpperCase() || '?';

  if (src && !errored) {
    return (
      <img
        src={src}
        alt={name ?? 'Employee'}
        onError={() => setErrored(true)}
        style={{
          width: size, height: size, borderRadius: '50%', objectFit: 'cover',
          border: '1px solid #e5e7eb', flexShrink: 0,
        }}
      />
    );
  }

  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg,#0f766e,#0d9488)', color: 'white',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.42), fontWeight: 700,
    }}>
      {initial}
    </div>
  );
};

export const SkeletonRow: React.FC<{ i: number; cols?: number }> = ({ i, cols = 8 }) => (
  <tr style={{ animationDelay: `${i * 0.05}s` }}>
    {[28, 140, 70, 70, 80, 90, 60, 70, 60, 90, 80].slice(0, cols).map((w, j) => (
      <td key={j}><div className="pr-shimmer" style={{ height: 14, width: w, maxWidth: '100%' }} /></td>
    ))}
  </tr>
);

// ─────────────────────────────────────────────────────────────────────────────
// PsSummaryCard — same shape/shadow language as the Students dashboard's info
// boxes, scaled up into standalone summary cards. (Named distinctly from the
// exported `StatCard` above, which other payroll files still depend on with
// its original prop signature.)
// ─────────────────────────────────────────────────────────────────────────────
const PsSummaryCard: React.FC<{
  label: string;
  value: string | number;
  sub?: string;
  icon: string;
  gradient: string;
}> = ({ label, value, sub, icon, gradient }) => (
  <div
    style={{
      background: 'white',
      borderRadius: 16,
      padding: '18px 20px',
      border: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      boxShadow: '0 2px 12px rgba(0,0,0,.06)',
    }}
  >
    <div
      style={{
        width: 46,
        height: 46,
        borderRadius: 12,
        flexShrink: 0,
        background: gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(30,64,175,.2)',
      }}
    >
      <i className={icon} style={{ fontSize: 19, color: 'white' }} />
    </div>
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#1f2937', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{sub}</div>}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// EmployeeSalaryList — unified table for every employee, whether or not they
// have an active salary definition, built with PrimeReact DataTable to match
// the Students dashboard exactly (including ProfilePictureHover for avatars).
// ─────────────────────────────────────────────────────────────────────────────
const EmployeeSalaryList: React.FC<{
  rows: UnifiedEmployeeRow[];
  loading: boolean;
  onEdit: (row: UnifiedEmployeeRow) => void;
  onAdd: () => void;
  roleFilter: string;
  onRoleFilterChange: (role: string) => void;
  statusFilter: SalaryStatusFilter;
  onStatusFilterChange: (status: SalaryStatusFilter) => void;
}> = ({ rows, loading, onEdit, onAdd, roleFilter, onRoleFilterChange, statusFilter, onStatusFilterChange }) => {
  const [search, setSearch] = useState('');

  const bySearch = search.trim()
    ? rows.filter(
        (r) =>
          String(r.employee_id).includes(search) ||
          (r.employee_code ?? '').toLowerCase().includes(search.toLowerCase()) ||
          r.employee_role.includes(search.toLowerCase()) ||
          (r.role ?? '').toLowerCase().includes(search.toLowerCase()) ||
          (r.employee_name ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : rows;

  const byStatus =
    statusFilter === 'all' ? bySearch : bySearch.filter((r) => (statusFilter === 'has' ? r.has_salary : !r.has_salary));

  const displayed =
    statusFilter === 'all' ? [...byStatus].sort((a, b) => Number(a.has_salary) - Number(b.has_salary)) : byStatus;

  const missingCount = rows.filter((r) => !r.has_salary).length;

  const serialTemplate = (_: UnifiedEmployeeRow, options: ColumnBodyOptions): JSX.Element => (
    <div
      style={{
        width: 28,
        height: 28,
        background: '#f9fafb',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 12,
        color: '#6b7280',
        margin: '0 auto',
      }}
    >
      {options.rowIndex + 1}
    </div>
  );

  const employeeTemplate = (r: UnifiedEmployeeRow): JSX.Element => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <ProfilePictureHover
        src={r.profile_picture ?? ''}
        name={r.employee_name ?? `Employee #${r.employee_id}`}
        cacheBust={r.updated_at ? new Date(r.updated_at).getTime().toString() : 'no-salary'}
      />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: '#1f2937',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {r.employee_name ?? `Employee #${r.employee_id}`}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>ID #{r.employee_code ?? r.employee_id}</div>
      </div>
    </div>
  );

  const payrollRoleTemplate = (r: UnifiedEmployeeRow): JSX.Element => (
    <div
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'capitalize',
        color: ROLE_COLORS[r.employee_role] ?? '#6b7280',
        background: `${ROLE_COLORS[r.employee_role] ?? '#6b7280'}18`,
        border: `1px solid ${ROLE_COLORS[r.employee_role] ?? '#6b7280'}30`,
      }}
    >
      {r.employee_role}
    </div>
  );

  const systemRoleTemplate = (r: UnifiedEmployeeRow): JSX.Element =>
    r.role ? (
      <div
        style={{
          display: 'inline-block',
          padding: '3px 10px',
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'capitalize',
          color: '#4338ca',
          background: 'rgba(99,102,241,.08)',
          border: '1px solid rgba(99,102,241,.18)',
        }}
      >
        {r.role}
      </div>
    ) : (
      <span style={{ fontSize: 12, color: '#9ca3af' }}>—</span>
    );

  const statusTemplate = (r: UnifiedEmployeeRow): JSX.Element =>
    r.has_salary ? (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '3px 10px',
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 700,
          color: '#15803d',
          background: 'rgba(34,197,94,.1)',
          border: '1px solid rgba(34,197,94,.2)',
        }}
      >
        <i className="pi pi-check" style={{ fontSize: 10 }} /> Set
      </div>
    ) : (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '3px 10px',
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 700,
          color: '#dc2626',
          background: 'rgba(239,68,68,.1)',
          border: '1px solid rgba(239,68,68,.2)',
        }}
      >
        <i className="pi pi-exclamation-triangle" style={{ fontSize: 10 }} /> Not set
      </div>
    );

  const salaryTemplate = (r: UnifiedEmployeeRow): JSX.Element => {
    if (!r.has_salary) return <span style={{ fontSize: 13, color: '#d1d5db' }}>—</span>;
    const allowanceTotal =
      Number(r.hra ?? 0) + Number(r.transport_allowance ?? 0) + Number(r.medical_allowance ?? 0) + Number(r.other_allowance ?? 0);
    const breakdown = `HRA: ${fmt(r.hra ?? 0)} · Transport: ${fmt(r.transport_allowance ?? 0)} · Medical: ${fmt(
      r.medical_allowance ?? 0
    )} · Other: ${fmt(r.other_allowance ?? 0)}`;
    return (
      <div title={breakdown}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>{fmt(r.basic_salary ?? 0)}</div>
        {allowanceTotal > 0 && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>+ {fmt(allowanceTotal)} allowances</div>}
      </div>
    );
  };

  const pfTemplate = (r: UnifiedEmployeeRow): JSX.Element => {
    if (!r.has_salary) return <span style={{ fontSize: 13, color: '#d1d5db' }}>—</span>;
    return r.pf_type === 'fixed' ? (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 700,
          background: 'rgba(59,130,246,.08)',
          color: '#1e40af',
          border: '1px solid rgba(59,130,246,.18)',
        }}
      >
        ₹ {fmt(r.provident_fund_amount ?? 0)}
      </div>
    ) : (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 700,
          background: 'rgba(99,102,241,.08)',
          color: '#4338ca',
          border: '1px solid rgba(99,102,241,.18)',
        }}
      >
        % {r.provident_fund_pct ?? 0}
      </div>
    );
  };

  const taxTemplate = (r: UnifiedEmployeeRow): JSX.Element => (
    <div style={{ fontSize: 13, color: r.has_salary ? '#1f2937' : '#d1d5db', fontWeight: 500 }}>
      {r.has_salary ? fmt(r.professional_tax ?? 0) : '—'}
    </div>
  );

  const tdsTemplate = (r: UnifiedEmployeeRow): JSX.Element => (
    <div style={{ fontSize: 13, color: r.has_salary ? '#1f2937' : '#d1d5db', fontWeight: 500 }}>
      {r.has_salary ? `${r.tds_pct ?? 0}%` : '—'}
    </div>
  );

  const effectiveFromTemplate = (r: UnifiedEmployeeRow): JSX.Element => (
    <div style={{ fontSize: 13, color: r.has_salary ? '#6b7280' : '#d1d5db', fontWeight: 500, whiteSpace: 'nowrap' }}>
      {r.has_salary ? r.effective_from : '—'}
    </div>
  );

  const actionTemplate = (r: UnifiedEmployeeRow): JSX.Element => (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      {r.has_salary ? (
        <button className="ps-edit-btn" onClick={() => onEdit(r)}>
          <i className="pi pi-pencil" style={{ fontSize: 11 }} /> Edit
        </button>
      ) : (
        <button className="ps-set-btn" onClick={() => onEdit(r)}>
          <i className="pi pi-plus" style={{ fontSize: 11 }} /> Set salary
        </button>
      )}
    </div>
  );

  return (
    <div
      style={{
        background: 'white',
        borderRadius: 20,
        boxShadow: '0 2px 12px rgba(0,0,0,.06)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid #f3f4f6',
          background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: '20px 20px 0 0',
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="pi pi-users" style={{ fontSize: 14, color: '#4169E1' }} />
          </div>
          <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
            {loading ? 'Loading…' : `${displayed.length} employees`}
          </span>
          {!loading && missingCount > 0 && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#dc2626',
                background: 'rgba(239,68,68,.08)',
                border: '1px solid rgba(239,68,68,.2)',
                borderRadius: 20,
                padding: '3px 10px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <i className="pi pi-exclamation-triangle" style={{ fontSize: 10 }} /> {missingCount} without salary
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="ps-select" value={statusFilter} onChange={(e) => onStatusFilterChange(e.target.value as SalaryStatusFilter)}>
            {SALARY_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select className="ps-select" value={roleFilter} onChange={(e) => onRoleFilterChange(e.target.value)}>
            {ROLE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button className="ps-add-btn" onClick={onAdd}>
            <i className="pi pi-plus" style={{ fontSize: 12 }} /> Add salary
          </button>
        </div>
      </div>

      <div className="ps-search-wrapper" style={{ margin: '16px 20px 0' }}>
        <i className="pi pi-search" style={{ fontSize: 15, color: '#3b82f6', flexShrink: 0 }} />
        <input type="text" placeholder="Search by name, employee ID, or role..." value={search} onChange={(e) => setSearch(e.target.value)} />
        {search && (
          <button className="ps-search-clear" onClick={() => setSearch('')}>
            <i className="pi pi-times" style={{ fontSize: 12 }} />
          </button>
        )}
      </div>

      <DataTable
        className="ps-datatable"
        value={displayed}
        loading={false}
        emptyMessage={
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', minHeight: 300 }}>
            <div
              style={{
                width: 56,
                height: 56,
                background: 'linear-gradient(135deg,#1e40af,#3b82f6,#60a5fa)',
                borderRadius: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
                boxShadow: '0 6px 18px rgba(59,130,246,.2)',
              }}
            >
              <i className="pi pi-wallet" style={{ fontSize: 25, color: 'white' }} />
            </div>
            <h3 style={{ margin: 0, color: '#1e40af', fontSize: 20, fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>No Employees Found</h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: 14, fontWeight: 500, textAlign: 'center', maxWidth: 400, lineHeight: 1.6 }}>
              Try a different filter or search term.
            </p>
          </div>
        }
      >
        <Column header="#" body={serialTemplate} style={{ width: 52 }} />
        <Column header="Employee" body={employeeTemplate} />
        <Column header="Payroll Role" body={payrollRoleTemplate} style={{ width: 120 }} />
        <Column header="System Role" body={systemRoleTemplate} style={{ width: 120 }} />
        <Column header="Status" body={statusTemplate} style={{ width: 110 }} />
        <Column header="Salary" body={salaryTemplate} />
        <Column header="PF" body={pfTemplate} style={{ width: 110 }} />
        <Column header="Prof. Tax" body={taxTemplate} style={{ width: 100 }} />
        <Column header="TDS %" body={tdsTemplate} style={{ width: 80 }} />
        <Column header="Effective From" body={effectiveFromTemplate} style={{ width: 130 }} />
        <Column header="Actions" body={actionTemplate} style={{ width: 130 }} />
      </DataTable>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PayrollManagement — "Salary Setup" page. Routed at /payroll-management.
// Handles the per-employee salary definitions table only; payroll runs and
// advances have moved to PayrollRecordsPage below.
// ─────────────────────────────────────────────────────────────────────────────
const PayrollManagement: React.FC = () => {
  const toast = useRef<Toast | null>(null);

  const [salaries, setSalaries] = useState<SalaryRow[]>([]);
  const [notGenerated, setNotGenerated] = useState<UngeneratedEmployee[]>([]);
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<SalaryStatusFilter>('all');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingSalary, setEditingSalary] = useState<UnifiedEmployeeRow | null>(null);

  const unifiedRows: UnifiedEmployeeRow[] = [
    ...salaries.map((s): UnifiedEmployeeRow => ({ ...s, has_salary: true })),
    ...notGenerated.map(
      (e): UnifiedEmployeeRow => ({
        id: null,
        employee_id: e.employee_id,
        employee_code: e.employee_code,
        employee_role: e.employee_role,
        employee_name: e.employee_name,
        employee_contact: e.employee_contact,
        profile_picture: e.profile_picture,
        role: e.role,
        has_salary: false,
        basic_salary: null,
        hra: null,
        transport_allowance: null,
        medical_allowance: null,
        other_allowance: null,
        pf_type: null,
        provident_fund_pct: null,
        provident_fund_amount: null,
        professional_tax: null,
        tds_pct: null,
        effective_from: null,
        status: null,
        created_at: null,
        updated_at: null,
      })
    ),
  ];

  const teacherCount = salaries.filter((s) => s.employee_role === 'teacher').length;
  const staffCount = salaries.filter((s) => s.employee_role === 'staff').length;
  const principalCount = salaries.filter((s) => s.employee_role === 'principal').length;
  const totalSalaryBill = salaries.reduce(
    (acc, s) => acc + Number(s.basic_salary) + Number(s.hra) + Number(s.transport_allowance) + Number(s.medical_allowance) + Number(s.other_allowance),
    0
  );

  const fetchSalaries = useCallback(async (role: string) => {
    setSalaryLoading(true);
    try {
      const res = await apiClient.get('/payroll/salary', {
        params: role ? { employee_role: role } : {},
      });
      if (res.data.status === 'success') {
        setSalaries(res.data.data ?? []);
        setNotGenerated(res.data.not_generated ?? []);
      }
    } catch (e: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || 'Failed to load salaries', life: 4000 });
    } finally {
      setSalaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSalaries(roleFilter);
  }, [fetchSalaries, roleFilter]);

  const openAdd = () => {
    setEditingSalary(null);
    setModalOpen(true);
  };

  const openEdit = (row: UnifiedEmployeeRow) => {
    setEditingSalary(row);
    setModalOpen(true);
  };

  const handleSaved = () => {
    setModalOpen(false);
    fetchSalaries(roleFilter);
    toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Salary definition saved.', life: 3000 });
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <style>{CSS}</style>
      <style>{SHELL_CSS}</style>

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <GlobalHeaderTitle
          title="Salary Setup"
          subtitle={salaryLoading ? 'Loading…' : `${salaries.length} active salary definitions`}
          icon="pi pi-wallet"
          buttons={[
            {
              label: 'Refresh',
              icon: 'pi pi-refresh',
              onClick: () => fetchSalaries(roleFilter),
              outlined: true,
              disabled: salaryLoading,
              style: { borderWidth: '2px' },
            },
            {
              label: 'Add Salary',
              icon: 'pi pi-plus',
              onClick: openAdd,
            },
          ]}
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 14, marginBottom: 20 }}>
          <PsSummaryCard
            label="Total Salary Bill"
            value={fmt(totalSalaryBill)}
            sub="Gross per month (before deductions)"
            icon="pi pi-wallet"
            gradient="linear-gradient(135deg,#1e40af,#3b82f6)"
          />
          <PsSummaryCard label="Teachers" value={teacherCount} icon="pi pi-users" gradient="linear-gradient(135deg,#1d4ed8,#60a5fa)" />
          <PsSummaryCard label="Staff" value={staffCount} icon="pi pi-users" gradient="linear-gradient(135deg,#6d28d9,#a78bfa)" />
          <PsSummaryCard label="Principals" value={principalCount} icon="pi pi-users" gradient="linear-gradient(135deg,#b45309,#fbbf24)" />
        </div>

        {salaryLoading ? (
          <PayrollManagementSkeletonLoader />
        ) : (
          <EmployeeSalaryList
            rows={unifiedRows}
            loading={salaryLoading}
            onEdit={openEdit}
            onAdd={openAdd}
            roleFilter={roleFilter}
            onRoleFilterChange={setRoleFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
        )}
      </div>

      {modalOpen && <PayrollSalaryModal existing={editingSalary} onSaved={handleSaved} onClose={() => setModalOpen(false)} toast={toast} />}
    </>
  );
};



export default PayrollManagement;